import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { PushNotificationProvider } from './contexts/PushNotificationContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import ParentLayout from './components/ParentLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Staff from './pages/Staff';
import Payments from './pages/Payments';
import Reports from './pages/Reports';
import Grades from './pages/Grades';
import Expenses from './pages/Expenses';
import Supplies from './pages/Supplies';
import Attendance from './pages/Attendance';
import AbsenceJustifications from './pages/AbsenceJustifications';
import AttendanceStats from './pages/AttendanceStats';
import StaffHome from './pages/StaffHome';
import Users from './pages/Users';
import RolePermissions from './pages/RolePermissions';
import Messages from './pages/Messages';
import ParentRegister from './pages/ParentRegister';
import ParentDashboard from './pages/ParentDashboard';
import ParentAppointments from './pages/ParentAppointments';
import ParentTimetable from './pages/ParentTimetable';
import SchoolSettings from './pages/SchoolSettings';
import Tools from './pages/Tools';
import Appointments from './pages/Appointments';
import Timetables from './pages/Timetables';
import Cursus from './pages/Cursus';
import Notifications from './pages/Notifications';
import CantineMenus from './pages/CantineMenus';
import TransportRoutes from './pages/TransportRoutes';
import DirectorDashboard from './pages/DirectorDashboard';
import YearEndPromotion from './pages/YearEndPromotion';
import ArchivedStudents from './pages/ArchivedStudents';
import Trash from './pages/Trash';
import HrDashboard from './pages/HrDashboard';
import DirectorKpi from './pages/DirectorKpi';
import MyAbsences from './pages/MyAbsences';
import MobileMyAbsences from './pages/mobile/MobileMyAbsences';
import Security from './pages/Security';
import DailyLists from './pages/DailyLists';
import SecretariatDashboard from './pages/SecretariatDashboard';
import AdminPerformance from './pages/AdminPerformance';
import ResetPassword from './pages/ResetPassword';
import AdminSecurity from './pages/AdminSecurity';
import AdminNotifications from './pages/AdminNotifications';
import AdminBulkOps from './pages/AdminBulkOps';
import AdminPushBroadcast from './pages/AdminPushBroadcast';
import ParentCantine from './pages/ParentCantine';
import ParentBusTracking from './pages/ParentBusTracking';
import MobileLogin from './pages/mobile/MobileLogin';
import MobileInstall from './pages/mobile/MobileInstall';
import DriverApp from './pages/mobile/DriverApp';
import CantineStaffApp from './pages/mobile/CantineStaffApp';
import MobileTeacherApp from './pages/mobile/MobileTeacherApp';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentError from './pages/PaymentError';
import VerifyJustification from './pages/VerifyJustification';
import VerifyAttestation from './pages/VerifyAttestation';
import SignPayslip from './pages/SignPayslip';
import SignBulletin from './pages/SignBulletin';
import BiometricPunch from './pages/BiometricPunch';
import ComplianceOverview from './pages/ComplianceOverview';
import BulletinComplianceOverview from './pages/BulletinComplianceOverview';
import PredictiveAnalytics from './pages/PredictiveAnalytics';
import AuditJournal from './pages/AuditJournal';
import SupportPlans from './pages/SupportPlans';
import PublicPlanBooking from './pages/PublicPlanBooking';
import AppointmentResponse from './pages/AppointmentResponse';
import PublicCohortSummary from './pages/PublicCohortSummary';
import PublicSelfJustify from './pages/PublicSelfJustify';
import StaffAbsences from './pages/StaffAbsences';
import BiometricKiosk from './pages/BiometricKiosk';
import './App.css';

// Default landing per role (when a role isn't allowed on the current route,
// or when the user navigates to "/")
const ROLE_HOME = {
  admin: '/',
  director_general: '/',
  comptable: '/',
  cashier: '/',
  director_studies: '/director',
  rh: '/hr',
  user: '/secretariat',
  parent: '/parent',
  transport_staff: '/m/driver',
  cantine_staff: '/m/cantine',
  teacher_user: '/m/teacher',
  staff_user: '/staff-home',
  vigil: '/security',
};
const homeForRole = (role) => ROLE_HOME[role] || '/students';
const CLEANING_BLOCKED_PATHS = ['/security', '/absences', '/attendance-stats'];

// Stable role tuples (module-level constants) — referenced by ProtectedRoute below.
// Defining them inline as `allowedRoles={['admin', ...]}` creates a NEW array on
// every render, which breaks React's referential equality (memoization defeats,
// useEffect deps with allowedRoles in scope would re-fire on every render, etc.).
const ROLES = Object.freeze({
  ADMIN_COMPTABLE_CASHIER: Object.freeze(['admin', 'comptable', 'cashier']),
  ADMIN_USER_DIRECTOR: Object.freeze(['admin', 'user', 'director_studies']),
  ADMIN_USER_RH: Object.freeze(['admin', 'user', 'rh']),
  ADMIN_CASHIER: Object.freeze(['admin', 'cashier']),
  ADMIN_DIRECTOR: Object.freeze(['admin', 'director_studies']),
  ADMIN_COMPTABLE: Object.freeze(['admin', 'comptable']),
  ADMIN_USER: Object.freeze(['admin', 'user']),
  ADMIN_USER_SECRETARY: Object.freeze(['admin', 'user', 'secretary']),
  ADMIN_RH: Object.freeze(['admin', 'rh']),
  ADMIN: Object.freeze(['admin']),
  ADMIN_DIRECTOR_USER: Object.freeze(['admin', 'director_studies', 'user']),
  ADMIN_DIRECTOR_USER_STAFF: Object.freeze(['admin', 'director_studies', 'user', 'staff_user']),
  ADMIN_DIRECTOR_RH: Object.freeze(['admin', 'director_studies', 'rh']),
  ADMIN_DIRECTOR_SECRETARY: Object.freeze(['admin', 'director_studies', 'secretary']),
});

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect parents to their portal
  if (user.role === 'parent') {
    return <Navigate to="/parent" replace />;
  }

  // Mobile staff (cantine/transport) → mobile-only routes
  if (user.role === 'transport_staff') {
    return <Navigate to="/m/driver" replace />;
  }
  if (user.role === 'cantine_staff') {
    return <Navigate to="/m/cantine" replace />;
  }
  if (user.role === 'teacher_user') {
    return <Navigate to="/m/teacher" replace />;
  }

  // Custom (admin-created) profiles are governed purely by their granted pages.
  if (user.is_custom_role) {
    const allowed = user.allowed_paths || [];
    const CUSTOM_ALWAYS = ['/notifications', '/my-absences'];
    const matches = (p) => p === '/'
      ? location.pathname === '/'
      : (location.pathname === p || location.pathname.startsWith(p + '/'));
    const pathOk = CUSTOM_ALWAYS.includes(location.pathname) || allowed.some(matches);
    if (pathOk) return <Layout>{children}</Layout>;
    const home = allowed[0];
    if (!home || location.pathname === home) {
      return (
        <Layout>
          <div className="p-8 text-center text-[#64748B]" data-testid="role-no-access">
            Aucune page accessible pour votre profil.
          </div>
        </Layout>
      );
    }
    return <Navigate to={home} replace />;
  }

  // iter102 — Cleaning agents: no student-absence / security pages even by direct URL.
  if (user.role === 'staff_user' && user.staff_type === 'cleaning'
      && CLEANING_BLOCKED_PATHS.includes(location.pathname)) {
    return <Navigate to="/my-absences" replace />;
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    // Directeur Général inherits the operational roles behind its interface.
    const DG_INHERIT = ['director_studies', 'rh', 'comptable', 'cashier', 'secretary', 'user'];
    const dgAllowed = user.role === 'director_general'
      && allowedRoles.some(r => DG_INHERIT.includes(r));
    if (!dgAllowed) {
    // Avoid infinite loop: if we're already on the role's home, render a fallback
    const home = homeForRole(user.role);
    if (location.pathname === home) {
      return (
        <Layout>
          <div className="p-8 text-center text-[#64748B]" data-testid="role-no-access">
            Aucune page accessible pour votre rôle.
          </div>
        </Layout>
      );
    }
    return <Navigate to={home} replace />;
    }
  }

  // iter96 — Vigil role: bypass sidebar Layout for a tablet-friendly UI.
  if (user.role === 'vigil') {
    // Force the vigil onto the security page whenever they wander elsewhere.
    if (location.pathname !== '/security'
        && location.pathname !== '/my-absences'
        && location.pathname !== '/logout') {
      return <Navigate to="/security" replace />;
    }
    return <>{children}</>;
  }

  return <Layout>{children}</Layout>;
};

const MobileAnyStaffRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role === 'parent') return <Navigate to="/m/login" replace />;
  return children;
};

const MobileStaffRoute = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/m/login" replace />;
  if (user.role !== role && user.role !== 'admin') {
    return <Navigate to="/m/login" replace />;
  }
  return children;
};

const ParentRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'parent') {
    return <Navigate to="/" replace />;
  }

  return <ParentLayout>{children}</ParentLayout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'parent') {
      return <Navigate to="/parent" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const navigate = useNavigate();

  // Listen for click-through from the push notification service worker.
  // When the user clicks a notification while the SPA is already open, the
  // service worker posts {type:'push.navigate', url} to focus the right page
  // without a full reload.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      const data = event.data;
      if (data && data.type === 'push.navigate' && data.url) {
        try {
          navigate(data.url);
        } catch (_) {
          window.location.href = data.url;
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/parent/register" element={<PublicRoute><ParentRegister /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Online payment return URLs (GeniusPay redirect targets) */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/error" element={<PaymentError />} />

      {/* iter50 — Public verification page for justificatif PDF QR code */}
      <Route path="/verify/justif/:token" element={<VerifyJustification />} />
      <Route path="/verify/attestation/:token" element={<VerifyAttestation />} />

      {/* iter56 — Public payslip signature page (token-scoped) */}
      <Route path="/sign-payslip/:token" element={<SignPayslip />} />

      {/* iter67 — Public bulletin signature page (parent/teacher/director) */}
      <Route path="/sign-bulletin/:token" element={<SignBulletin />} />

      {/* iter84 — Public parent booking (support plan → 1-click RDV) */}
      <Route path="/book-plan-appointment/:token" element={<PublicPlanBooking />} />

      {/* iter86 — Public appointment confirm/decline (from J-1 reminder) */}
      <Route path="/appt-response/:token" element={<AppointmentResponse />} />

      {/* iter86 — Public anonymous cohort summary (website widget) */}
      <Route path="/cohort-summary" element={<PublicCohortSummary />} />

      {/* iter88 — Public self-justify for pending staff absences */}
      <Route path="/justify-absence/:token" element={<PublicSelfJustify />} />

      {/* iter54 — Public Kiosque terminal (fullscreen, no chrome) */}
      <Route path="/kiosk" element={<BiometricKiosk />} />
      
      {/* Mobile staff (cantine + transport) */}
      <Route path="/m/login" element={<MobileLogin />} />
      <Route path="/m/install" element={<MobileInstall />} />
      <Route path="/download" element={<MobileInstall />} />
      <Route path="/m/driver" element={<MobileStaffRoute role="transport_staff"><DriverApp /></MobileStaffRoute>} />
      <Route path="/m/cantine" element={<MobileStaffRoute role="cantine_staff"><CantineStaffApp /></MobileStaffRoute>} />

      {/* Mobile teacher app (PIN 6-digit) */}
      <Route path="/m/teacher/*" element={<MobileStaffRoute role="teacher_user"><MobileTeacherApp /></MobileStaffRoute>} />
      
      {/* Parent Routes */}
      <Route path="/parent" element={
        <ParentRoute>
          <ParentDashboard />
        </ParentRoute>
      } />

      <Route path="/parent/appointments" element={
        <ParentRoute>
          <ParentAppointments />
        </ParentRoute>
      } />

      <Route path="/parent/timetable" element={
        <ParentRoute>
          <ParentTimetable />
        </ParentRoute>
      } />

      <Route path="/parent/cantine" element={
        <ParentRoute>
          <ParentCantine />
        </ParentRoute>
      } />

      <Route path="/parent/transport" element={
        <ParentRoute>
          <ParentBusTracking />
        </ParentRoute>
      } />
      
      {/* Admin/Staff Routes */}
      <Route path="/" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_COMPTABLE_CASHIER}>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/students" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_USER_DIRECTOR}>
          <Students />
        </ProtectedRoute>
      } />
      
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_USER_RH}>
          <Staff />
        </ProtectedRoute>
      } />
      
      <Route path="/payments" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_CASHIER}>
          <Payments />
        </ProtectedRoute>
      } />
      
      <Route path="/grades" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_USER}>
          <Grades />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_COMPTABLE}>
          <Reports />
        </ProtectedRoute>
      } />
      
      <Route path="/expenses" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_COMPTABLE}>
          <Expenses />
        </ProtectedRoute>
      } />
      
      <Route path="/supplies" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_USER}>
          <Supplies />
        </ProtectedRoute>
      } />
      
      <Route path="/attendance" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_RH}>
          <Attendance />
        </ProtectedRoute>
      } />

      <Route path="/absences" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_USER_STAFF}>
          <AbsenceJustifications />
        </ProtectedRoute>
      } />

      <Route path="/attendance-stats" element={
        <ProtectedRoute allowedRoles={[...ROLES.ADMIN_DIRECTOR_USER_STAFF, 'director_general']}>
          <AttendanceStats />
        </ProtectedRoute>
      } />

      <Route path="/staff-home" element={
        <ProtectedRoute allowedRoles={Object.freeze(['staff_user'])}>
          <StaffHome />
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <Users />
        </ProtectedRoute>
      } />

      <Route path="/role-permissions" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <RolePermissions />
        </ProtectedRoute>
      } />
      
      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_USER}>
          <Messages />
        </ProtectedRoute>
      } />

      <Route path="/appointments" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_RH}>
          <Appointments />
        </ProtectedRoute>
      } />

      <Route path="/timetables" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR}>
          <Timetables />
        </ProtectedRoute>
      } />

      <Route path="/students/:studentId/cursus" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_USER}>
          <Cursus />
        </ProtectedRoute>
      } />

      <Route path="/notifications" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <Notifications />
        </ProtectedRoute>
      } />

      <Route path="/cantine-menus" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_USER}>
          <CantineMenus />
        </ProtectedRoute>
      } />

      <Route path="/director" element={
        <ProtectedRoute allowedRoles={[...ROLES.ADMIN_DIRECTOR, 'director_general']}>
          <DirectorDashboard />
        </ProtectedRoute>
      } />

      <Route path="/year-end" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR}>
          <YearEndPromotion />
        </ProtectedRoute>
      } />

      <Route path="/archived-students" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_SECRETARY}>
          <ArchivedStudents />
        </ProtectedRoute>
      } />

      <Route path="/trash" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <Trash />
        </ProtectedRoute>
      } />

      <Route path="/hr" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_RH}>
          <HrDashboard />
        </ProtectedRoute>
      } />

      {/* iter92 — Director real-time KPI dashboard */}
      <Route path="/director-kpi" element={
        <ProtectedRoute allowedRoles={[...ROLES.ADMIN, 'director_general']}>
          <DirectorKpi />
        </ProtectedRoute>
      } />

      {/* iter92 — Self-service "my absences" for staff/teacher accounts */}
      <Route path="/my-absences" element={
        <ProtectedRoute allowedRoles={['admin', 'rh', 'director_studies', 'comptable', 'cashier', 'secretary', 'user', 'staff_user', 'teacher_user', 'vigil']}>
          <MyAbsences />
        </ProtectedRoute>
      } />
      {/* iter102 — Mobile shell for PIN-based staff (teachers, cantine, transport) */}
      <Route path="/m/my-absences" element={<MobileAnyStaffRoute><MobileMyAbsences /></MobileAnyStaffRoute>} />

      {/* iter95 — Security page (visitors + incidents) */}
      <Route path="/security" element={
        <ProtectedRoute allowedRoles={['admin', 'cashier', 'staff_user', 'user', 'vigil']}>
          <Security />
        </ProtectedRoute>
      } />

      {/* iter97 — Daily lists (canteen + transport) */}
      <Route path="/daily-lists" element={
        <ProtectedRoute allowedRoles={['admin', 'comptable', 'cashier', 'user', 'director_general']}>
          <DailyLists />
        </ProtectedRoute>
      } />

      {/* iter53 — Biometric time-punch (fingerprint) — HR/Admin */}
      <Route path="/biometric-punch" element={
        <ProtectedRoute allowedRoles={[...ROLES.ADMIN_RH, 'director_general']}>
          <BiometricPunch />
        </ProtectedRoute>
      } />

      {/* iter61 — Executive compliance overview (12-month payroll analytics) */}
      <Route path="/compliance-overview" element={
        <ProtectedRoute allowedRoles={['admin', 'rh', 'comptable', 'director_general']}>
          <ComplianceOverview />
        </ProtectedRoute>
      } />

      {/* iter68 — Bulletin (report card) compliance overview */}
      <Route path="/bulletin-compliance" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_RH}>
          <BulletinComplianceOverview />
        </ProtectedRoute>
      } />

      {/* iter75 — Predictive Analytics per subject (at-risk students) */}
      <Route path="/predictive-analytics" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_RH}>
          <PredictiveAnalytics />
        </ProtectedRoute>
      } />

      {/* iter82 — Pedagogical Support Plans */}
      <Route path="/support-plans" element={
        <ProtectedRoute allowedRoles={[...ROLES.ADMIN_DIRECTOR, 'director_general']}>
          <SupportPlans />
        </ProtectedRoute>
      } />

      {/* iter87 — Staff absences (justify → paid, reject → deducted) */}
      <Route path="/staff-absences" element={
        <ProtectedRoute allowedRoles={[...ROLES.ADMIN_RH, 'director_general']}>
          <StaffAbsences />
        </ProtectedRoute>
      } />

      {/* iter78 — Unified audit journal (SLA + calendar + payroll) */}
      <Route path="/audit-journal" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_DIRECTOR_RH}>
          <AuditJournal />
        </ProtectedRoute>
      } />

      <Route path="/secretariat" element={
        <ProtectedRoute allowedRoles={[...ROLES.ADMIN_USER_SECRETARY, 'director_general']}>
          <SecretariatDashboard />
        </ProtectedRoute>
      } />

      <Route path="/transport-routes" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN_USER}>
          <TransportRoutes />
        </ProtectedRoute>
      } />

      <Route path="/tools" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <Tools />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <SchoolSettings />
        </ProtectedRoute>
      } />

      <Route path="/admin/performance" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <AdminPerformance />
        </ProtectedRoute>
      } />

      <Route path="/admin/security" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <AdminSecurity />
        </ProtectedRoute>
      } />

      <Route path="/admin/notifications" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <AdminNotifications />
        </ProtectedRoute>
      } />

      <Route path="/admin/bulk-operations" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <AdminBulkOps />
        </ProtectedRoute>
      } />

      <Route path="/admin/push-broadcast" element={
        <ProtectedRoute allowedRoles={ROLES.ADMIN}>
          <AdminPushBroadcast />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <PushNotificationProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </PushNotificationProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
